<template>
  <form @submit.prevent="handleSubmit">
    <div class="mb-3">
      <label for="title" class="form-label">Title</label>
      <input type="text" class="form-control" id="title" v-model="localSubtask.title" required>
    </div>
    <div class="mb-3">
      <label for="number" class="form-label">Number</label>
      <input type="number" class="form-control" id="number" v-model="localSubtask.number" required>
    </div>
    <div class="mb-3">
      <label for="to_evaluate" class="form-label">Evaluate: </label>
      <input type="checkbox" class="form-check-input form-check-lg" id="to_evaluate" v-model="localSubtask.to_evaluate"><br>
      <small class="form-text text-muted">Uncheck if this task is not to be evaluated.</small>
    </div>
    <div class="mb-3">
      <label for="description" class="form-label">Description</label>
      <textarea type="textarea" class="form-control" id="description" rows="8" v-model="localSubtask.description" required></textarea>
    </div>
    <!-- Display file paths with download and delete buttons -->
    <div class="mb-3">
      <label class="form-label">Resource Files</label>
      <input type="file" class="form-control mb-2" id="resource_files" multiple @change="handleFileChange('resource_files', $event)">
      <FileList 
        v-if="localSubtask.resource_file_paths && localSubtask.resource_file_paths.length > 0"	
        title="Resource Files" 
        :files="localSubtask.resource_file_paths"
        :filesMetadata="localSubtask.resource_files_metadata"
        :onViewFile="showFileContent" 
        :onDownloadFile="downloadFile" 
        :onDeleteFile="file => deleteFile(file, 'resource_file_paths')" 
      />
    </div>
    <div class="mb-3">
      <label class="form-label">Submission Files</label>
      <input type="file" class="form-control mb-2" id="submission_files" multiple @change="handleFileChange('submission_files', $event)">
      <FileList
        v-if="localSubtask.submission_file_paths && localSubtask.submission_file_paths.length > 0"
        title="Submission Files" 
        :files="localSubtask.submission_file_paths"
        :filesMetadata="localSubtask.submission_files_metadata"
        :onViewFile="showFileContent" 
        :onDownloadFile="downloadFile" 
        :onDeleteFile="file => deleteFile(file, 'submission_file_paths')" 
      />
    </div>
    <div class="mb-3">
      <label class="form-label">Test Files</label>
      <input type="file" class="form-control mb-2" id="test_files" multiple @change="handleFileChange('test_files', $event)">
      <FileList 
        v-if="localSubtask.test_file_paths && localSubtask.test_file_paths.length > 0"
        title="Test Files" 
        :files="localSubtask.test_file_paths"
        :filesMetadata="localSubtask.test_files_metadata"
        :onViewFile="showFileContent" 
        :onDownloadFile="downloadFile" 
        :onDeleteFile="file => deleteFile(file, 'test_file_paths')" 
      />
    </div>
    <div class="mb-3">
      <label class="form-label">Pre-check Files</label>
      <input type="file" class="form-control mb-2" id="precheck_files" multiple @change="handleFileChange('precheck_files', $event)">
      <FileList 
        v-if="localSubtask.test_pre_check_file_paths && localSubtask.test_pre_check_file_paths.length > 0"
        title="Pre-check Files"
        :files="localSubtask.test_pre_check_file_paths"
        :filesMetadata="localSubtask.test_pre_check_files_metadata"
        :onViewFile="showFileContent" 
        :onDownloadFile="downloadFile" 
        :onDeleteFile="file => deleteFile(file, 'test_pre_check_file_paths')"
      />
    </div>
    <button type="submit" class="btn btn-primary">{{ buttonText }}</button>
  </form>

  <StemErrorModal ref="stemErrorModal"/>
</template>

<script>
import SubtaskController from '@/controllers/SubtaskController';
import FileList from '@/components/FilesList.vue';
import StemErrorModal from './StemErrorModal.vue';

export default {
  components: {
    FileList,
    StemErrorModal
  },
  props: {
    subtask: {
      type: Object,
      required: true,
    },
    buttonText: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      localSubtask: { ...this.subtask },
      resource_files: [],
      submission_files: [],
      test_files: [],
      precheck_files: [],
      filesToDelete: {
        resource_file_paths: [],
        submission_file_paths: [],
        test_file_paths: [],
        test_pre_check_file_paths: [],
      }
    };
  },
  watch: {
    subtask: {
      handler(newSubtask) {
        this.localSubtask = { ...newSubtask };
      },
      deep: true,
    },
  },
  methods: {
    handleSubmit() {
      this.$emit('submitForm', {
        ...this.localSubtask,
        resource_files: this.resource_files,
        submission_files: this.submission_files,
        test_files: this.test_files,
        precheck_files: this.precheck_files,
        filesToDelete: this.filesToDelete
      });
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
    async showFileContent(filePath) {
      try {
        const response = await SubtaskController.downloadFile(filePath);
        // open file in new tab
        window.open(URL.createObjectURL(new Blob([response.data])));
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await SubtaskController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', 'Error downloading file');
      }
    },
    // Download single file
    deleteFile(filePath, fileType) {
      this.filesToDelete[fileType].push(filePath);
      this.localSubtask[fileType] = this.localSubtask[fileType].filter(file => file !== filePath);
    },
    handleFileChange(type, event) {
      this[type] = event.target.files;
    }
  },
};
</script>